import React, { useEffect, useRef, useState } from 'react'
import { useT } from '../i18n/index.js'
import { useOnlineStatus } from '../hooks/useOffline.js'
import { getPendingCount } from '../lib/offlineStorage.js'

type BannerState = 'offline' | 'syncing' | 'synced' | 'hidden'

interface OfflineIndicatorProps {
  /** Externally controlled sync state — set to 'syncing' while POST /api/sync is in flight */
  syncState?: 'idle' | 'syncing' | 'done' | 'error'
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ syncState = 'idle' }) => {
  const t = useT()
  const { isOnline, wasOffline } = useOnlineStatus()
  const [bannerState, setBannerState] = useState<BannerState>('hidden')
  const [pendingCount, setPendingCount] = useState<number>(0)
  // Ref pour le timer anti-flash : on n'affiche le bandeau qu'après 3s offline confirmé
  const offlineDelayTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Poll pending count while offline so the badge stays up to date
  useEffect(() => {
    if (!isOnline) {
      getPendingCount().then(setPendingCount).catch(() => {})
      const interval = setInterval(() => {
        getPendingCount().then(setPendingCount).catch(() => {})
      }, 3_000)
      return () => clearInterval(interval)
    }
    return undefined
  }, [isOnline])

  // Drive banner state machine
  useEffect(() => {
    // Offline : afficher le bandeau uniquement si navigator.onLine === false
    // ET seulement après 3s (anti-flash SW cluster)
    if (!isOnline && navigator.onLine === false) {
      if (offlineDelayTimer.current === null) {
        offlineDelayTimer.current = setTimeout(() => {
          offlineDelayTimer.current = null
          if (!navigator.onLine) {
            setBannerState('offline')
          }
        }, 3_000)
      }
      return () => {
        if (offlineDelayTimer.current !== null) {
          clearTimeout(offlineDelayTimer.current)
          offlineDelayTimer.current = null
        }
      }
    }

    // Online → annuler tout timer en attente et gérer les transitions
    if (offlineDelayTimer.current !== null) {
      clearTimeout(offlineDelayTimer.current)
      offlineDelayTimer.current = null
    }

    if (syncState === 'syncing') {
      setBannerState('syncing')
      return
    }
    if (syncState === 'done' && wasOffline) {
      setBannerState('synced')
      const timer = setTimeout(() => setBannerState('hidden'), 3_000)
      return () => clearTimeout(timer)
    }
    if (syncState === 'error' && wasOffline) {
      // Keep showing error briefly
      const timer = setTimeout(() => setBannerState('hidden'), 5_000)
      return () => clearTimeout(timer)
    }
    if (isOnline && !wasOffline && bannerState !== 'hidden') {
      setBannerState('hidden')
    }
    return undefined
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, wasOffline, syncState])

  const visible = bannerState !== 'hidden'

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('pwa.offlineMode')}
      className={`
        fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out
        ${visible ? 'translate-y-0' : '-translate-y-full'}
      `}
    >
      <div className={`
        flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium
        shadow-md
        ${bannerState === 'offline' ? 'bg-amber-400 text-amber-900' : ''}
        ${bannerState === 'syncing' ? 'bg-blue-500 text-white' : ''}
        ${bannerState === 'synced' ? 'bg-primary-light0 text-white' : ''}
        ${bannerState === 'hidden' ? 'bg-amber-400 text-amber-900' : ''}
      `}>

        {bannerState === 'offline' && (
          <>
            <WifiOffIcon />
            <span>{t('pwa.offlineMode')}</span>
            {pendingCount > 0 && (
              <span className="ml-2 bg-amber-700/20 text-amber-900 text-xs font-semibold px-2 py-0.5 rounded-full">
                {pendingCount} {t('pwa.syncPending')}
              </span>
            )}
          </>
        )}

        {bannerState === 'syncing' && (
          <>
            <SpinnerIcon />
            <span>{t('pwa.syncing')}</span>
          </>
        )}

        {bannerState === 'synced' && (
          <>
            <CheckIcon />
            <span>{t('pwa.syncComplete')}</span>
          </>
        )}
      </div>
    </div>
  )
}

// ── Inline icons (no extra dep) ────────────────────────────────────────────

const WifiOffIcon: React.FC = () => (
  <svg
    className="w-4 h-4 shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden="true"
  >
    <line x1="1" y1="1" x2="23" y2="23" />
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.56 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01" />
  </svg>
)

const SpinnerIcon: React.FC = () => (
  <svg
    className="w-4 h-4 shrink-0 animate-spin"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
)

const CheckIcon: React.FC = () => (
  <svg
    className="w-4 h-4 shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
)
